import React, { useState, useCallback, useEffect, useRef, ChangeEvent } from 'react';
import { Form, Button, Table, Row, Col } from 'react-bootstrap';
import { Transaction, Statement } from '../../types';
import { Plus, Trash, Save, X } from 'react-bootstrap-icons';

interface StatementEditorProps {
  statement: Statement;
  onSave: (updatedStatement: Statement) => void;
  onCancel: () => void;
  onDelete?: () => void;
}

const StatementEditor: React.FC<StatementEditorProps> = ({
  statement,
  onSave,
  onCancel,
  onDelete
}) => {
  const [editedStatement, setEditedStatement] = useState<Statement>({ ...statement });
  // Initialize new transaction with statement date
  const [newTransaction, setNewTransaction] = useState<Partial<Transaction>>({
    date: statement.statement_date || '',
    description: '',
    amount: 0,
    transaction_type: 'debit'
  });

  // Update new transaction date when statement date changes
  useEffect(() => {
    if (editedStatement.statement_date) {
      setNewTransaction(prev => ({
        ...prev,
        date: editedStatement.statement_date
      }));
    }
  }, [editedStatement.statement_date]);
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [addingTransaction, setAddingTransaction] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    date?: string;
    description?: string;
    amount?: string;
  }>({});

  const originalStatement = useRef(statement);

  const hasUnsavedChanges = useCallback(() => {
    const compareObjects = (obj1: any, obj2: any): boolean => {
      if (Array.isArray(obj1) && Array.isArray(obj2)) {
        if (obj1.length !== obj2.length) return true;
        return obj1.some((item, index) => compareObjects(item, obj2[index]));
      }
      
      if (typeof obj1 === 'object' && typeof obj2 === 'object') {
        const keys1 = Object.keys(obj1 || {});
        const keys2 = Object.keys(obj2 || {});
        if (keys1.length !== keys2.length) return true;
        return keys1.some(key => compareObjects(obj1[key], obj2[key]));
      }
      
      return obj1 !== obj2;
    };

    return compareObjects(editedStatement, originalStatement.current);
  }, [editedStatement]);

  // Function to generate a summary of changes
  const getChangesSummary = useCallback(() => {
    const changes: string[] = [];
    const orig = originalStatement.current;
    
    // Check basic fields
    if (editedStatement.bank_name !== orig.bank_name) {
      changes.push(`• Bank name changed from "${orig.bank_name}" to "${editedStatement.bank_name}"`);
    }
    if (editedStatement.account_number !== orig.account_number) {
      changes.push(`• Account number changed from "${orig.account_number}" to "${editedStatement.account_number}"`);
    }
    if (editedStatement.statement_date !== orig.statement_date) {
      changes.push(`• Statement date changed from "${orig.statement_date}" to "${editedStatement.statement_date}"`);
    }
    if (editedStatement.beginning_balance !== orig.beginning_balance) {
      changes.push(`• Beginning balance changed from $${orig.beginning_balance} to $${editedStatement.beginning_balance}`);
    }
    if (editedStatement.ending_balance !== orig.ending_balance) {
      changes.push(`• Ending balance changed from $${orig.ending_balance} to $${editedStatement.ending_balance}`);
    }

    // Check transactions
    const origTxCount = orig.transactions.length;
    const newTxCount = editedStatement.transactions.length;
    
    if (newTxCount > origTxCount) {
      changes.push(`• Added ${newTxCount - origTxCount} new transaction(s)`);
    } else if (newTxCount < origTxCount) {
      changes.push(`• Removed ${origTxCount - newTxCount} transaction(s)`);
    }

    // Check modified transactions
    const minLength = Math.min(origTxCount, newTxCount);
    for (let i = 0; i < minLength; i++) {
      const origTx = orig.transactions[i];
      const newTx = editedStatement.transactions[i];
      
      if (origTx.date !== newTx.date || 
          origTx.description !== newTx.description ||
          origTx.amount !== newTx.amount ||
          origTx.transaction_type !== newTx.transaction_type) {
        changes.push(`• Modified transaction: ${origTx.date} ${origTx.description}`);
      }
    }

    return changes;
  }, [editedStatement]);

  // Add beforeunload event handler
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleStatementChange = (field: keyof Statement, value: string | number) => {
    setEditedStatement((prev: Statement) => ({ ...prev, [field]: value }));
  };

  const handleTransactionChange = (index: number, field: keyof Transaction, value: string | number) => {
    const updatedTransactions = [...editedStatement.transactions];
    updatedTransactions[index] = { ...updatedTransactions[index], [field]: value };
    setEditedStatement((prev: Statement) => ({ ...prev, transactions: updatedTransactions }));
  };

  const handleDeleteTransaction = (index: number) => {
    const updatedTransactions = editedStatement.transactions.filter((_: Transaction, i: number) => i !== index);
    setEditedStatement((prev: Statement) => ({ ...prev, transactions: updatedTransactions }));
  };

  const handleAddTransaction = () => {
    // Clear previous validation errors
    setValidationErrors({});
    
    // Validate required fields
    const errors: { date?: string; description?: string; amount?: string } = {};
    if (!newTransaction.date) {
      errors.date = 'Date is required';
    }
    if (!newTransaction.description) {
      errors.description = 'Description is required';
    }
    if (newTransaction.amount === undefined || newTransaction.amount === null) {
      errors.amount = 'Amount is required';
    }

    // If there are validation errors, show them and return
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // Start loading state
    setAddingTransaction(true);

    try {
      const transaction: Transaction = {
        transaction_id: `new-${Date.now()}`,
        date: newTransaction.date || '',
        description: newTransaction.description || '',
        amount: Number(newTransaction.amount) || 0,
        transaction_type: newTransaction.transaction_type as 'credit' | 'debit'
      };

      setEditedStatement((prev: Statement) => ({
        ...prev,
        transactions: [...prev.transactions, transaction]
      }));

      // Reset form but keep the statement date
      setNewTransaction({
        date: editedStatement.statement_date || '',
        description: '',
        amount: 0,
        transaction_type: 'debit'
      });
      setShowAddTransaction(false);
    } finally {
      setAddingTransaction(false);
    }
  };

  const handleSave = () => {
    if (!hasUnsavedChanges()) {
      onSave(editedStatement);
      return;
    }

    const changes = getChangesSummary();
    const message = changes.length > 0 
      ? `The following changes will be saved:\n\n${changes.join('\n')}\n\nDo you want to continue?`
      : 'Save changes?';

    if (window.confirm(message)) {
      // Sort transactions by date before saving
      const sortedTransactions = [...editedStatement.transactions].sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      
      onSave({
        ...editedStatement,
        transactions: sortedTransactions
      });
    }
  };

  return (
    <div className="statement-editor">
      <div className="mb-4">
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Bank Name</Form.Label>
              <Form.Control
                type="text"
                value={editedStatement.bank_name}
                onChange={e => handleStatementChange('bank_name', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Account Number</Form.Label>
              <Form.Control
                type="text"
                value={editedStatement.account_number}
                onChange={e => handleStatementChange('account_number', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Statement Date</Form.Label>
              <Form.Control
                type="date"
                value={editedStatement.statement_date?.split('T')[0]}
                onChange={e => handleStatementChange('statement_date', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Beginning Balance</Form.Label>
                                <Form.Control
                    type="number"
                    step="0.01"
                    min="-999999999999.99"
                    value={editedStatement.beginning_balance}
                    onChange={e => handleStatementChange('beginning_balance', Number(e.target.value))}
                  />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Ending Balance</Form.Label>
                                <Form.Control
                    type="number"
                    step="0.01"
                    min="-999999999999.99"
                    value={editedStatement.ending_balance}
                    onChange={e => handleStatementChange('ending_balance', Number(e.target.value))}
                  />
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="mb-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Transactions</h6>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowAddTransaction(true)}
            className="d-flex align-items-center gap-1"
          >
            <Plus size={16} /> Add Transaction
          </Button>
        </div>

        {showAddTransaction && (
          <div className="mb-3 p-3 border rounded">
            <h6 className="mb-3">New Transaction</h6>
            <Row>
              <Col md={2}>
                <Form.Group className="mb-2">
                  <Form.Label>Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={newTransaction.date}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setValidationErrors(prev => ({ ...prev, date: undefined }));
                      setNewTransaction((prev: Partial<Transaction>) => ({ ...prev, date: e.target.value }));
                    }}
                    isInvalid={!!validationErrors.date}
                  />
                  {validationErrors.date && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.date}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-2">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    type="text"
                    value={newTransaction.description}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setValidationErrors(prev => ({ ...prev, description: undefined }));
                      setNewTransaction((prev: Partial<Transaction>) => ({ ...prev, description: e.target.value }));
                    }}
                    isInvalid={!!validationErrors.description}
                  />
                  {validationErrors.description && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.description}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-2">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={newTransaction.transaction_type}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => 
                      setNewTransaction((prev: Partial<Transaction>) => ({ ...prev, transaction_type: e.target.value as 'credit' | 'debit' }))
                    }
                  >
                    <option value="debit">Debit</option>
                    <option value="credit">Credit</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className="mb-2">
                  <Form.Label>Amount</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={newTransaction.amount}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      setValidationErrors(prev => ({ ...prev, amount: undefined }));
                      setNewTransaction((prev: Partial<Transaction>) => ({ ...prev, amount: Number(e.target.value) }));
                    }}
                    isInvalid={!!validationErrors.amount}
                  />
                  {validationErrors.amount && (
                    <Form.Control.Feedback type="invalid">
                      {validationErrors.amount}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={2} className="d-flex align-items-end">
                <div className="d-flex gap-2 mb-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAddTransaction}
                    className="flex-grow-1"
                    disabled={addingTransaction}
                  >
                    {addingTransaction ? 'Adding...' : 'Add'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowAddTransaction(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </Col>
            </Row>
          </div>
        )}

        <div className="table-responsive">
          <Table hover size="sm">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>Type</th>
                <th className="text-end">Amount</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {editedStatement.transactions.map((tx: Transaction, index: number) => (
                <tr key={tx.transaction_id}>
                  <td>
                    <Form.Control
                      type="date"
                      size="sm"
                      value={tx.date?.split('T')[0]}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => 
                        handleTransactionChange(index, 'date', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={tx.description}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => 
                        handleTransactionChange(index, 'description', e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={tx.transaction_type}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => 
                        handleTransactionChange(index, 'transaction_type', e.target.value as 'credit' | 'debit')
                      }
                    >
                      <option value="debit">Debit</option>
                      <option value="credit">Credit</option>
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      value={tx.amount}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => 
                        handleTransactionChange(index, 'amount', Number(e.target.value))
                      }
                      className="text-end"
                    />
                  </td>
                  <td className="text-end">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0 border-0"
                      onClick={() => handleDeleteTransaction(index)}
                    >
                      <Trash size={16} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <div className="d-flex gap-2 justify-content-end mt-4">
        {onDelete && (
          <Button
            variant="outline-danger"
            onClick={() => {
              const message = `Are you sure you want to delete this statement?\n\nBank: ${editedStatement.bank_name}\nAccount: ${editedStatement.account_number}\nDate: ${editedStatement.statement_date}\n\nThis action cannot be undone.`;
              if (window.confirm(message)) {
                onDelete();
              }
            }}
            className="d-flex align-items-center gap-1"
          >
            <Trash size={16} /> Delete Statement
          </Button>
        )}
        <Button
          variant="outline-secondary"
          onClick={() => {
            if (!hasUnsavedChanges() || window.confirm('You have unsaved changes. Are you sure you want to cancel?')) {
              onCancel();
            }
          }}
          className="d-flex align-items-center gap-1"
        >
          <X size={16} /> Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          className="d-flex align-items-center gap-1"
        >
          <Save size={16} /> Save Changes
        </Button>
      </div>
    </div>
  );
};

export default StatementEditor;